import { useMemo, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import {
  Plus,
  FileSignature,
  Search,
  Loader2,
  ChevronLeft,
  ChevronRight,
  Trash2,
  MoreHorizontal,
  TrendingUp,
  Clock,
} from 'lucide-react';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { KPI } from '@/components/ui/kpi';
import { Pill } from '@/components/ui/pill';
import { Eyebrow } from '@/components/ui/eyebrow';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { useAuth } from '@/lib/auth';
import {
  useContratos,
  useContratosAggregate,
  useDeleteContrato,
  type Contrato,
} from '@/lib/hooks/contratos';
import { ContratoDrawer } from '@/features/contratos/ContratoDrawer';
import { fmtBRL, fmtCpfCnpj, fmtDate } from '@/lib/utils';

export const Route = createFileRoute('/_app/lancamentos/contratos')({
  component: ContratosPage,
});

const PAGE_SIZE = 25;

const TIPO_LABEL: Record<string, string> = {
  PRESTACAO_SERVICOS: 'Prestação de serviços',
  FORNECIMENTO: 'Fornecimento',
  LOCACAO: 'Locação',
  OUTRO: 'Outro',
};

function isEncerrado(contrato: Contrato): boolean {
  if (!contrato.dataFim) return false;
  return new Date(contrato.dataFim) < new Date();
}

function ContratosPage() {
  const { user } = useAuth();
  const workspaceId = user?.effectiveWorkspaceId ?? null;

  const [search, setSearch] = useState('');
  const [offset, setOffset] = useState(0);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [openRow, setOpenRow] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<Contrato | null>(null);

  const filters = useMemo(
    () => ({
      search: search.trim() || undefined,
      limit: PAGE_SIZE,
      offset,
    }),
    [search, offset],
  );

  const listQ = useContratos(workspaceId, filters);
  const aggQ = useContratosAggregate(workspaceId);
  const delMut = useDeleteContrato(workspaceId);

  const items = listQ.data?.items ?? [];
  const total = listQ.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const currentPage = Math.floor(offset / PAGE_SIZE) + 1;
  const canPrev = offset > 0;
  const canNext = offset + PAGE_SIZE < total;

  async function handleDelete() {
    if (!confirmDelete) return;
    try {
      await delMut.mutateAsync(confirmDelete.id);
      toast.success('Contrato removido.');
      setConfirmDelete(null);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Falha ao remover.');
    }
  }

  if (!workspaceId) {
    return (
      <Card variant="default" className="p-8 text-center">
        <p className="text-sm text-fg-muted">Selecione um workspace para gerenciar contratos.</p>
      </Card>
    );
  }

  return (
    <div className="flex flex-col gap-5">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <Eyebrow>Lançamento · Mensal</Eyebrow>
          <h1 className="font-display text-[34px] md:text-[42px] font-bold tracking-[-0.03em] text-fg leading-[1.0] mt-1">
            Contratos
          </h1>
          <p className="text-sm text-fg-muted mt-2">Contratos com prestadores, fornecedores e parceiros</p>
        </div>
        <Button variant="primary" size="lg" onClick={() => setDrawerOpen(true)}>
          <Plus className="h-4 w-4" strokeWidth={1.8} />
          Novo contrato
        </Button>
      </header>

      <section className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <KPI
          tone="mint"
          label="Ativos"
          value={String(aggQ.data?.ativos ?? 0)}
          delta={aggQ.data?.ativos === 1 ? 'contrato' : 'contratos'}
          icon={FileSignature}
        />
        <KPI
          tone="lilac"
          label="Valor total contratado"
          value={fmtBRL(aggQ.data?.valorTotal ?? '0')}
          delta="contratos cadastrados"
          icon={TrendingUp}
        />
        <KPI
          tone="clay"
          label="Vencendo em 30 dias"
          value={String(aggQ.data?.vencendoEm30Dias ?? 0)}
          delta="próximos vencimentos"
          icon={Clock}
        />
      </section>

      <Card variant="default" className="p-4">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[200px] max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-fg-muted" strokeWidth={1.8} />
            <Input
              placeholder="Buscar por contratado…"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setOffset(0);
              }}
              className="pl-9"
            />
          </div>
        </div>
      </Card>

      <div className="rounded-3 border border-card-stroke bg-card shadow-1 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-card-stroke bg-card-soft/60">
                <th className="px-4 py-3 text-left font-mono text-[10px] uppercase tracking-[0.10em] text-fg-muted">Início</th>
                <th className="px-4 py-3 text-left font-mono text-[10px] uppercase tracking-[0.10em] text-fg-muted">Contratado</th>
                <th className="px-4 py-3 text-left font-mono text-[10px] uppercase tracking-[0.10em] text-fg-muted">Objeto</th>
                <th className="px-4 py-3 text-right font-mono text-[10px] uppercase tracking-[0.10em] text-fg-muted">Valor</th>
                <th className="px-4 py-3 text-left font-mono text-[10px] uppercase tracking-[0.10em] text-fg-muted">Status</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {listQ.isLoading && (
                <tr>
                  <td colSpan={6} className="px-4 py-10 text-center text-fg-muted">
                    <Loader2 className="inline h-4 w-4 animate-spin mr-2" strokeWidth={1.8} />
                    Carregando…
                  </td>
                </tr>
              )}
              {!listQ.isLoading && items.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-10 text-center text-fg-muted text-sm">
                    Nenhum contrato encontrado.
                  </td>
                </tr>
              )}
              {items.map((c) => {
                const encerrado = isEncerrado(c);
                return (
                  <tr key={c.id} className="border-b border-card-stroke/50 last:border-b-0 hover:bg-card-soft/40">
                    <td className="px-4 py-3 text-sm text-fg whitespace-nowrap">
                      {fmtDate(c.dataInicio)}
                      {c.dataFim && (
                        <p className="text-[11px] text-fg-muted">até {fmtDate(c.dataFim)}</p>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-fg truncate">{c.contratadoNome}</p>
                        <p className="text-[11px] text-fg-muted font-mono">{fmtCpfCnpj(c.contratadoCpfCnpj)}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="min-w-0 max-w-[240px]">
                        <p className="text-sm text-fg truncate">{c.objeto}</p>
                        <p className="text-[11px] text-fg-muted">{TIPO_LABEL[c.tipo] ?? c.tipo}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-sm text-fg tabular-nums">
                      {fmtBRL(c.valorTotal)}
                    </td>
                    <td className="px-4 py-3">
                      <Pill tone={encerrado ? 'block' : 'pass'} dot>
                        {encerrado ? 'encerrado' : 'ativo'}
                      </Pill>
                    </td>
                    <td className="px-4 py-3 text-right relative">
                      <button
                        type="button"
                        onClick={() => setOpenRow(openRow === c.id ? null : c.id)}
                        className="grid h-8 w-8 ml-auto place-items-center rounded-1 text-fg-muted hover:text-fg hover:bg-card-soft"
                        aria-label="Ações"
                      >
                        <MoreHorizontal className="h-4 w-4" strokeWidth={1.8} />
                      </button>
                      {openRow === c.id && (
                        <>
                          <div className="fixed inset-0 z-30" onClick={() => setOpenRow(null)} aria-hidden />
                          <div className="absolute right-2 top-10 z-40 w-40 rounded-2 border border-card-stroke bg-card shadow-clay p-1">
                            <button
                              type="button"
                              onClick={() => {
                                setOpenRow(null);
                                setConfirmDelete(c);
                              }}
                              className="flex w-full items-center gap-2 rounded-1 px-2 py-1.5 text-sm text-warn hover:bg-warn-soft"
                            >
                              <Trash2 className="h-4 w-4" strokeWidth={1.8} />
                              Excluir
                            </button>
                          </div>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {total > 0 && (
          <div className="flex items-center justify-between gap-2 border-t border-card-stroke px-4 py-3 bg-card-soft/40">
            <p className="text-xs text-fg-muted">
              Página <span className="text-fg font-medium">{currentPage}</span> de{' '}
              <span className="text-fg font-medium">{totalPages}</span> · {total}{' '}
              {total === 1 ? 'contrato' : 'contratos'}
            </p>
            <div className="flex items-center gap-1">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={!canPrev}
                onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
              >
                <ChevronLeft className="h-4 w-4" strokeWidth={1.8} />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={!canNext}
                onClick={() => setOffset(offset + PAGE_SIZE)}
              >
                <ChevronRight className="h-4 w-4" strokeWidth={1.8} />
              </Button>
            </div>
          </div>
        )}
      </div>

      <ContratoDrawer workspaceId={workspaceId} open={drawerOpen} onOpenChange={setDrawerOpen} />

      <ConfirmDialog
        open={!!confirmDelete}
        onOpenChange={(v) => !v && setConfirmDelete(null)}
        tone="danger"
        title="Excluir este contrato?"
        description={
          confirmDelete
            ? `${confirmDelete.contratadoNome} · ${fmtCpfCnpj(confirmDelete.contratadoCpfCnpj)}. Não dá pra desfazer.`
            : undefined
        }
        confirmLabel="Excluir"
        loading={delMut.isPending}
        onConfirm={handleDelete}
      />
    </div>
  );
}
